<template>
  <div class="foodleap-tp antialiased pb-24">

    <!-- Hero Section -->
    <section class="pt-24 pb-20 tp-hero-gradient relative overflow-hidden">
      <div class="max-w-4xl mx-auto px-8 text-center relative z-10">
        <span class="tp-badge tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase animate-on-scroll">
          MONTHLY INTELLIGENCE
        </span>
        <h1 class="text-5xl md:text-7xl font-bold tracking-tight tp-text-slate mb-8 leading-[1.1] animate-on-scroll delay-1 mt-6">
          Pulse
        </h1>
        <p class="text-xl tp-text-slate-70 font-light max-w-2xl mx-auto mb-12 animate-on-scroll delay-2">
          Every month, we distill the most important signals from the food system transition so you never miss a shift that matters.
        </p>
        <div class="tw-flex flex-wrap justify-center gap-4 animate-on-scroll delay-3">
          <a
            href="#pricing"
            class="tp-btn-primary no-underline tw-flex items-center gap-2 px-10 py-4 rounded-full font-bold">
            Get access
            <Icon name="arrow-right"/>
          </a>
        </div>
      </div>
      <div class="tp-blob absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-20"></div>
      <div class="tp-blob-2 absolute -bottom-20 -left-20 w-80 h-80 rounded-full blur-3xl opacity-15"></div>
    </section>

    <!-- What's inside (compact strip) -->
    <section class="py-8 bg-white border-b border-tp-blg animate-on-scroll">
      <div class="max-w-5xl mx-auto px-8">
        <div class="tw-flex flex-wrap items-center justify-center gap-3">
          <span class="text-xs font-bold tp-text-slate-60 uppercase tracking-widest">Each edition covers:</span>
          <div v-for="(item, i) in insideItems" :key="i"
               class="tw-flex items-center gap-2 px-4 py-2 rounded-full tp-pill text-sm font-semibold">
            <Icon :name="item.icon" size="13" class="tp-text-yellow-icon"/>
            {{ item.title }}
          </div>
        </div>
      </div>
    </section>

    <!-- Latest Pulse -->
    <section class="py-20 tp-bg-light">
      <div class="max-w-4xl mx-auto px-8">
        <div class="text-center mb-10 animate-on-scroll">
          <span class="text-xs font-bold tracking-widest uppercase tp-text-yellow block mb-4">April 2026</span>
          <h2 class="text-4xl font-bold tp-text-slate mb-4">Pulse</h2>
          <p class="tp-text-slate-60 text-lg font-light">A snapshot of this month's key signals from the food system transition.</p>
        </div>
        <div class="bg-white rounded-2xl border border-tp-blg shadow-sm overflow-hidden animate-on-scroll delay-1">

          <!-- Category tabs -->
          <div class="p-4 border-b border-tp-blg">
            <div class="tw-flex gap-1 bg-gray-50 rounded-xl p-1">
              <button
                v-for="cat in sampleCats"
                :key="cat.key"
                @click="activeSampleCat = cat.key"
                :class="[
                  'flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                  activeSampleCat === cat.key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                ]">
                {{ cat.label }}
              </button>
            </div>
          </div>

          <!-- Content -->
          <div class="p-8 space-y-8">

            <!-- Key Judgment -->
            <div class="space-y-2">
              <p class="text-[11px] font-bold tracking-widest uppercase tp-text-yellow m-0">Key Judgment</p>
              <p class="text-sm text-gray-700 leading-relaxed m-0">{{ sampleData.key_judgment }}</p>
            </div>

            <!-- Why this month matters -->
            <div class="space-y-2">
              <p class="text-[11px] font-bold tracking-widest uppercase tp-text-yellow m-0">Why this month matters</p>
              <p class="text-sm text-gray-700 leading-relaxed m-0">{{ sampleData.why_this_matters }}</p>
            </div>

            <!-- Driving signals -->
            <div>
              <p class="text-[11px] font-bold tracking-widest uppercase tp-text-yellow mb-4 m-0">What is driving the transition now</p>
              <div class="grid gap-4 sm:grid-cols-2">
                <div
                  v-for="group in drivingGroups"
                  :key="group.key"
                  class="rounded-xl border border-tp-blg bg-gray-50/60 p-4">
                  <p class="text-xs font-semibold tp-text-yellow mb-3 m-0">{{ group.label }}</p>
                  <div class="space-y-1">
                    <div
                      v-for="(signal, i) in sampleData.driving_signals[group.key]"
                      :key="i"
                      class="rounded-lg"
                      :class="{ 'bg-gray-50': expandedSignals[group.key + '_' + i] }">
                      <!-- Signal row -->
                      <div
                        class="tw-flex items-start gap-1.5 px-2 py-1.5 -mx-2 rounded-lg transition-colors"
                        :class="signal.articles && signal.articles.length ? 'cursor-pointer hover:bg-gray-100' : ''"
                        @click="signal.articles && signal.articles.length && toggleSignal(group.key, i)">
                        <span :class="['text-sm font-bold shrink-0 w-5 text-center leading-snug mt-px', trendColorClass(signal.trend)]">
                          {{ trendArrow(signal.trend) }}
                        </span>
                        <div class="flex-1 min-w-0">
                          <span class="text-sm font-semibold text-gray-900 leading-snug">{{ signal.name }}</span>
                          <p v-if="signal.meta" class="text-xs text-gray-400 m-0 mt-0.5">{{ signal.meta }}</p>
                        </div>
                        <svg
                          v-if="signal.articles && signal.articles.length"
                          class="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1 transition-transform duration-200"
                          :class="{ 'rotate-180': expandedSignals[group.key + '_' + i] }"
                          fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </div>
                      <!-- Articles -->
                      <div
                        v-if="signal.articles && signal.articles.length && expandedSignals[group.key + '_' + i]"
                        class="mt-1 pb-2 px-2 space-y-1">
                        <div
                          v-for="(article, ai) in signal.articles"
                          :key="ai"
                          class="tw-flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-gray-100">
                          <svg class="w-3 h-3 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                          </svg>
                          <span class="text-xs text-gray-700 leading-snug flex-1 min-w-0 truncate">{{ article.title }}</span>
                          <span class="text-xs text-gray-400 shrink-0 font-medium whitespace-nowrap">{{ article.source }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Actors in motion -->
            <div v-if="sampleData.actors && sampleData.actors.length">
              <p class="text-[11px] font-bold tracking-widest uppercase tp-text-yellow mb-3 m-0">Change makers in motion</p>
              <div class="bg-gray-50/60 rounded-xl border border-tp-blg divide-y divide-gray-100 overflow-hidden">
                <div
                  v-for="(actor, i) in sampleData.actors"
                  :key="i"
                  class="tw-flex items-center gap-3 px-4 py-3">
                  <div class="w-1.5 h-1.5 rounded-full tp-bullet-dot shrink-0"/>
                  <div class="min-w-0 flex-1">
                    <span class="text-sm font-semibold text-gray-900">{{ actor.name }}</span>
                    <p v-if="actor.description" class="text-xs text-gray-400 m-0 mt-0.5">{{ actor.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Blurred bottom + get access (locked categories only) -->
          <!--          <div v-if="!isFirstCat" class="relative border-t border-tp-blg">
                      <div class="p-8 space-y-3 blur-sm select-none pointer-events-none">
                        <div class="h-12 rounded-xl bg-gray-100 w-full"></div>
                        <div class="h-12 rounded-xl bg-gray-100 w-full opacity-70"></div>
                        <div class="h-12 rounded-xl bg-gray-100 w-full opacity-40"></div>
                      </div>
                      <div class="absolute inset-0 tw-flex items-center justify-center tp-blur-overlay">
                        <button
                          @click="$router.push('/intelligence')"
                          class="tp-btn-primary tw-flex items-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm shadow-lg">
                          Get access to the full report
                          <Icon name="arrow-right"/>
                        </button>
                      </div>
                    </div>-->

        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section class="py-20 tp-bg-slate" id="pricing">
      <div class="max-w-5xl mx-auto px-8">
        <div class="text-center mb-10 animate-on-scroll">
          <h2 class="text-3xl font-bold text-white mb-3">Get full access</h2>
          <p class="text-white/50 font-light">Everything you need to follow the food system transition.</p>
        </div>
        <div class="grid md:grid-cols-3 gap-6 items-center">
          <!-- Innovation Challenges -->
          <div class="tp-price-card rounded-2xl p-8 animate-on-scroll">
            <h3 class="text-base font-bold text-white mb-1">Innovation Challenges</h3>
            <p class="text-white/40 text-xs mb-6">Co-design open innovation with startups</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">€7,500</span>
              <span class="text-white/40 text-sm ml-1">/ challenge</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="tp-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Ecosystem search</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="tp-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Partner matchmaking</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="tp-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Managed pilot design</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="tp-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">
              Talk to us
            </button>
          </div>

          <!-- Ecosystem Insights (highlighted) -->
          <div class="tp-price-card rounded-2xl p-10 relative animate-on-scroll delay-1 tp-price-card-featured">
            <div class="tp-popular-badge absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black tracking-widest uppercase">
              Most Popular
            </div>
            <h3 class="text-xl font-bold text-white text-center mb-1 mt-6">Ecosystem Insights</h3>
            <p class="text-white/40 text-sm text-center mb-8">All transition reports + live ecosystem data</p>
            <div class="text-center mb-8">
              <span class="text-5xl font-black text-white">€9,000</span>
              <span class="text-white/40 text-base ml-2">/ year</span>
            </div>
            <ul class="space-y-3 mb-10">
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="tp-check-icon shrink-0"/>
                <span class="text-white text-sm">Pulse — monthly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="tp-check-icon shrink-0"/>
                <span class="text-white text-sm">Radar — quarterly</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="tp-check-icon shrink-0"/>
                <span class="text-white text-sm">Deep Dive Reports — annual</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="tp-check-icon shrink-0"/>
                <span class="text-white text-sm">Full ecosystem data access</span>
              </li>
              <li class="tw-flex items-center gap-3">
                <Icon name="check-circle" size="15" class="tp-check-icon shrink-0"/>
                <span class="text-white text-sm">Access to a transition event</span>
              </li>
            </ul>
            <button @click="$router.push(`/login?redirect_uri=${encodeURIComponent('/transition-reports/trial')}`)"
                    class="tp-btn-yellow w-full py-4 rounded-full font-bold text-base">Subscribe now
            </button>
          </div>

          <!-- Transition Advisory -->
          <div class="tp-price-card rounded-2xl p-8 animate-on-scroll delay-2">
            <h3 class="text-base font-bold text-white mb-1">Transition Advisory</h3>
            <p class="text-white/40 text-xs mb-6">Bespoke strategy for your journey</p>
            <div class="mb-6">
              <span class="text-3xl font-black text-white">Project</span>
              <span class="text-white/40 text-sm ml-1">based</span>
            </div>
            <ul class="space-y-2 mb-8">
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="tp-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Bespoke deep dives</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="tp-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Strategy workshop</span>
              </li>
              <li class="tw-flex items-center gap-2">
                <Icon name="check-circle" size="13" class="tp-check-icon shrink-0"/>
                <span class="text-white/60 text-xs">Executive coaching</span>
              </li>
            </ul>
            <button @click="$router.push({ path: '/about', hash: '#contact' })" class="tp-btn-ghost w-full py-2.5 rounded-full font-semibold text-sm">
              Talk to us
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA / Conversion box -->
    <section class="py-24 tp-bg-slate" style="background-color: rgba(33,36,74,0.97)">
      <div class="max-w-4xl mx-auto px-8 animate-on-scroll">
        <div class="tw-flex flex-col md:flex-row items-center gap-12">
          <div class="flex-1 text-center md:text-left">
            <span class="tp-badge tw-inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-6">
              Monthly Intelligence
            </span>
            <h2 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight mt-6">
              Never miss a shift that matters
            </h2>
            <p class="text-white/60 text-lg font-light mb-10">
              Monthly delivery, curated by our analysts. Included in Ecosystem Insights.
            </p>
            <div class="tw-flex flex-wrap gap-4 justify-center md:justify-start">
              <button
                @click="$router.push(`/login?redirect_uri=${encodeURIComponent('/transition-reports/trial')}`)"
                class="tp-btn-yellow tw-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-lg">
                Get access
                <Icon name="arrow-right"/>
              </button>
              <button
                @click="$router.push('/deep-dive-report')"
                class="tp-btn-secondary-dark px-10 py-4 rounded-full font-bold">
                Try a free Deep Dive
              </button>
            </div>
          </div>
          <!-- Mini signal preview -->
          <div class="flex-1 w-full max-w-sm">
            <div class="tp-preview-card rounded-2xl p-6 space-y-3">
              <div class="text-xs font-bold tracking-widest uppercase text-white/40 mb-4">April 2026 signals</div>
              <div v-for="(s, i) in latestSignals.slice(0, 3)" :key="i" class="tw-flex items-start gap-3">
                <div class="w-7 h-7 rounded-lg tp-inside-icon shrink-0 tw-flex items-center justify-center mt-0.5">
                  <Icon :name="s.icon" size="11"/>
                </div>
                <p class="text-white/70 text-xs leading-snug">{{ s.text }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Report Switcher Bar -->
    <div class="tp-rs-bar fixed bottom-6 left-1/2 -translate-x-1/2 z-50 tw-flex items-center gap-1 px-2 py-2 rounded-full shadow-2xl">
      <button @click="$router.push('/transition-pulse-report')" class="tp-rs-tab tp-rs-active tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="wave-square" size="13"/>
        Pulse
      </button>
      <button @click="$router.push('/innovation-radar-report')" class="tp-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="bullseye" size="13"/>
        Radar
      </button>
      <button @click="$router.push('/deep-dive-report')" class="tp-rs-tab tw-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold">
        <Icon name="file-text" size="13"/>
        Deep Dive
      </button>
    </div>

    <SimplifiedFooterFoodleapNew/>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import SimplifiedFooterFoodleapNew from '@/components/SimplifiedFooter/SimplifiedFooterFoodleapNew.vue'
  import Icon from '../Icon/Icon.vue'

  export default defineComponent({
    name: 'StaticHomeFoodleapTransitionPulsePage',
    components: { SimplifiedFooterFoodleapNew, Icon },

    computed: {
      sampleData () {
        return this.allSampleData[this.activeSampleCat]
      },
    },

    watch: {
      activeSampleCat () {
        this.expandedSignals = {}
      },
    },

    methods: {
      toggleSignal (groupKey, index) {
        const key = groupKey + '_' + index
        this.expandedSignals = { ...this.expandedSignals, [key]: !this.expandedSignals[key] }
      },
      trendArrow (trend) {
        const map = { 'double-up': '↑↑', up: '↑', neutral: '→', down: '↓', 'double-down': '↓↓' }
        return map[trend] || '→'
      },
      trendColorClass (trend) {
        if (trend === 'up' || trend === 'double-up') return 'text-green-600'
        if (trend === 'down' || trend === 'double-down') return 'text-red-500'
        return 'text-gray-400'
      },
    },

    data () {
      return {
        activeSampleCat: 'protein',
        expandedSignals: {},
        sampleCats: [
          { key: 'protein', label: 'Protein' },
          { key: 'upcycling', label: 'Upcycling' },
          { key: 'health', label: 'Health' },
          { key: 'circular', label: 'Circular' },
        ],
        drivingGroups: [
          { key: 'capital_investment', label: 'Capital & Investment' },
          { key: 'technology_innovation', label: 'Technology & Innovation' },
          { key: 'policy_regulatory', label: 'Policy & Regulatory' },
          { key: 'consumer_market', label: 'Consumer & Market' },
        ],
        allSampleData: {
          protein: {
            title: 'Alternative proteins  / AI for protein business / Hybrid (protein) products in BE / NL / FR / Balanced Proteins in NL, BE and FR / Plant / Animal based protein in food products / Protein Transition Radar',
            key_judgment: 'The alternative protein landscape in Europe is shifting towards advanced technologies and industrial scaling, as evidenced by significant investments like Danone\'s €1 billion acquisition of Huel and Standing Ovation\'s €30 million funding for precision fermentation. This signals a growing commitment to overcoming barriers in taste, texture, and nutritional density, moving beyond basic plant-based offerings. The trend is gaining ground, particularly in high-tech protein research, which will shape future product development. Companies should prioritize sourcing technologies that enhance protein formulations and consider partnerships with firms leading in precision fermentation and cellular agriculture.',
            why_this_matters: 'Starbucks launched protein lattes featuring Alpro\'s high-protein soy milk in Central Europe. Standing Ovation raised €30 million to commercialize precision fermentation dairy proteins. The UK government plans to implement a new food strategy emphasizing alternative proteins.',
            driving_signals: {
              capital_investment: [
                {
                  trend: 'double-up', name: 'Danone\'s Huel acquisition', meta: '€1 billion investment in plant protein',
                  articles: [
                    { title: 'Danone\'s makes logical - but pricey - protein push with Huel deal - Just Drinks', source: 'Just Drinks' },
                    { title: 'Danone and Arcor announce joint venture in Argentina\'s dairy market', source: 'Food Ingredients First' },
                    { title: 'Danone Buys Huel In €1 Billion Deal As Consumers Seek Functional Plant Protein', source: 'Plant Based News' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Standing Ovation funding', meta: 'Raised €30 million for dairy proteins',
                  articles: [
                    { title: 'Host Defense® Expands MycoBenefits® Line with New Formulas Uniquely Designed for Women\'s Wellness* | Weekly Voice', source: 'Weekly Voice' },
                    { title: 'Supermarket meat found to contain plant-based fillers, cutting production costs by 4.4%', source: 'NL Times' },
                    { title: 'Impossible Foods komt naar Nederland', source: 'Marketing Tribune' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Impossible Foods expansion', meta: 'New products launched in Netherlands',
                  articles: [
                    { title: 'Enter your email address to Win 1 Yr Home Plan', source: 'Legalese Decoder' },
                    {
                      title: 'Bpifrance\u2011led France 2030 fund and Danone Ventures back Standing Ovation\'s €30M to turn dairy waste into protein  --  TFN',
                      source: 'Tech Funding News'
                    },
                    { title: 'Standing Ovation: $34.2 Million Raised For Precision Fermentation Dairy Protein Scale-Up', source: 'Pulse2' },
                    { title: 'Standing Ovation secures $34.2m to scale precision-fermented dairy proteins  | FoodBev Media', source: 'FoodBev Media' },
                    { title: 'Standing Ovation nets $34m, gears up for US launch of casein via precision fermentation', source: 'AgFunder News' },
                    { title: 'Paris-based Standing Ovation raises €30 million to produce dairy proteins through precision fermentation | EU-Startups', source: 'EU-Startups' },
                    { title: 'Bel, Danone & French Govt Join $34.2M Funding for Standing Ovation', source: 'Green Queen' },
                    { title: 'Standing Ovation raises €30M to scale precision fermentation dairy proteins globally', source: 'Tech.eu' },
                  ],
                },
                {
                  trend: 'down', name: 'Ambienta\'s acquisition', meta: 'Invested in The Bridge for plant-based',
                  articles: [
                    { title: 'Fermentation startups raised billions -- now most of them can\'t find a manufacturer', source: 'Veg Out Mag' },
                  ],
                },
              ],
              technology_innovation: [
                {
                  trend: 'double-up', name: 'Starbucks protein lattes', meta: 'Introduced high-protein soy milk',
                  articles: [
                    { title: 'Starbucks Brings Protein Drinks to Europe, Including Alpro Soy Milk Lattes', source: 'Green Queen' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Standing Ovation casein', meta: 'Advancing precision fermentation technology',
                  articles: [
                    {
                      title: 'Bpifrance\u2011led France 2030 fund and Danone Ventures back Standing Ovation\'s €30M to turn dairy waste into protein  --  TFN',
                      source: 'Tech Funding News'
                    },
                    { title: 'Standing Ovation nets $34m, gears up for US launch of casein via precision fermentation', source: 'AgFunder News' },
                    { title: 'Bel, Danone & French Govt Join $34.2M Funding for Standing Ovation', source: 'Green Queen' },
                  ],
                },
                {
                  trend: 'neutral', name: 'Schouten chicken fillet', meta: 'Launched meat-like plant-based product',
                  articles: [
                    { title: 'Fermentation startups raised billions -- now most of them can\'t find a manufacturer', source: 'Veg Out Mag' },
                  ],
                },
                {
                  trend: 'up', name: 'Meala FoodTech protein', meta: 'Texturized pea protein for reformulation',
                  articles: [
                    { title: 'Enter your email address to Win 1 Yr Home Plan', source: 'Legalese Decoder' },
                    { title: 'Danone backs proteins supplier Standing Ovation', source: 'Just Food' },
                  ],
                },
              ],
              policy_regulatory: [
                {
                  trend: 'down', name: 'EU protein diversification', meta: 'Negotiating financial support framework',
                  articles: [
                    { title: 'Swiss Court Bans Use of \'Milk\' on Plant-Based Alternatives, Rejecting Danone\'s Appeal', source: 'Green Queen' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Danone-Arcor joint venture', meta: 'Driving innovation in Argentina',
                  articles: [
                    { title: 'Danone and Arcor announce joint venture in Argentina\'s dairy market', source: 'Food Ingredients First' },
                  ],
                },
                {
                  trend: 'up', name: 'UK food strategy investment', meta: '£100 million for alternative proteins',
                  articles: [
                    { title: 'How France is leading Europe\'s bioeconomy push', source: 'Sifted' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Food Standards Agency sandbox', meta: 'Facilitating cultivated meat production',
                  articles: [
                    { title: 'Biotech lobby groups are set to trap farmers and breeders in patent minefield', source: 'Corporate Europe' },
                    { title: 'From feed to food: Financing farmers\' protein diversification', source: 'EPC' },
                  ],
                },
              ],
              consumer_market: [
                {
                  trend: 'double-up', name: 'Impossible Foods demand', meta: 'Responding to plant-based interest',
                  articles: [
                    { title: 'Petfood Forum 2026: dsm-firmenich to unveil microalgal oil with same omega-3 ratio as fish oil', source: 'Nutrition Insight' },
                    { title: 'Future Food Quick Bites: Cultivated Pet Food, Khloe Kardashian & Nutella Peanut', source: 'Green Queen' },
                    { title: 'Starbucks Brings Protein Drinks to Europe, Including Alpro Soy Milk Lattes', source: 'Green Queen' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Starbucks high-protein trend', meta: 'Consumer interest in protein beverages',
                  articles: [
                    { title: 'Impossible Foods komt naar Nederland', source: 'Marketing Tribune' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Dairy alternatives growth', meta: 'Market projected to double by 2036',
                  articles: [
                    {
                      title: 'Feed Additives Market Set to Reach $67.90 Billion by 2031 Driven by Rising Demand for Animal Nutrition - Business Upturn',
                      source: 'Business Upturn'
                    },
                    { title: 'Pea Fiber Market to Reach USD 3.12 Billion by 2035 as Demand for Clean-Label and High-Fiber Ingredients Expands', source: 'OpenPR' },
                    { title: 'Plant-based Protein Market Global Business Trends and Forecast Study 2026 to 2035', source: 'OpenPR' },
                    { title: 'Organic Food Market Expands with Blockchain Traceability and Clean-Label Demand, Reaching USD 659.5 Billion by 2035', source: 'OpenPR' },
                    { title: 'Fermented Foods Market Driven by Probiotic Demand to Reach USD 1672.7 Billion by 2035', source: 'OpenPR' },
                    {
                      title: 'Colostrum Market is projected to Hit US$ 6.46 Billion by 2033 | Emerging Companies - PanTheryx, Colostrum BioTec GmbH, Cure Nutraceuticals Pvt. Ltd, Biostrum Nutritech Pvt. Ltd., Sterling Technology',
                      source: 'OpenPR'
                    },
                    { title: 'Dairy Products Market Size, Share and Report 2026-2035', source: 'OpenPR' },
                    { title: 'Egg White Substitute Market to Reach USD 2.55 Billion by 2035 as Demand for Vegan and Allergen-Free Food Products Expands', source: 'OpenPR' },
                    {
                      title: 'Plant-Based Food Market to Reach USD 49.5 Billion by 2036 Amid Rising Demand for Sustainable and Clean-Label Nutrition | Future Market Insights, Inc.',
                      source: 'Finance News'
                    },
                    { title: 'Non-Dairy Ice Cream Market to Hit US$10.1 bn by 2033 on Vegan Food Demand Boom Growth', source: 'OpenPR' },
                    { title: 'Dairy-Free Oat Bedtime Drink Market to Hit US$4.0 Bn by 2033 on Wellness Demand', source: 'OpenPR' },
                    {
                      title: 'Lactoferrin Market Set for Steady Expansion to US$ 762.00 Million by 2033, Driven by Rising Immune Health Demand and Infant Nutrition Growth, with North America Holding the Largest 32% Share, Led by Fonterra Co-operative Group Limited, Glanbia plc, and Fr',
                      source: 'OpenPR'
                    },
                    { title: 'Sports Nutrition Market Transforming Fitness Industry Through Rising Health Awareness and Active Lifestyle Adoption', source: 'OpenPR' },
                    {
                      title: 'Stress Relief Supplements Market Expected to Reach US$ 1,070.27 Million by 2033 as Rising Mental Health Awareness Drives Demand for Natural Wellness Solutions',
                      source: 'OpenPR'
                    },
                    { title: 'Natural Aroma Chemicals Market to Reach USD 5.8 Billion by 2035, Driven by Clean-Label Demand and Biotech-Based Production', source: 'OpenPR' },
                    { title: 'Report: Insect protein pet food market poised for rapid growth', source: 'Petfoodindustry' },
                    {
                      title: 'Insect-Based Animal Feed Market to Reach USD 2.3 Billion by 2035 Driven by Sustainable Protein Demand and Circular Economy Adoption',
                      source: 'OpenPR'
                    },
                    { title: 'Vegan Protein Bars Market to Hit 2.9 Bn by 2033 Driven by Rising Plant-Based Demand', source: 'OpenPR' },
                    { title: 'Protein Crisps Market to Reach USD 7.42 Billion by 2035 as Demand for High-Protein, Functional Snacks Accelerates by FMI', source: 'OpenPR' },
                    { title: 'Plant-Based Seafood Market to Hit US$1,014.0 Mn by 2033 Driven by Vegan Trends', source: 'OpenPR' },
                    { title: 'Plant-Based Meat Market Expected to Reach USD 33.3 Billion by 2031, at a CAGR of 20.5%: Allied Market Research', source: 'Finance News' },
                    { title: 'Zero Sugar Beverages Market to Reach US$10.3 Bn by 2033 Driven by Health Trends', source: 'OpenPR' },
                    { title: 'Paper Goods Market Set for Steady Expansion, Reaching USD 405.83 Billion by 2036 Amid Rising Demand for Hygiene', source: 'OpenPR' },
                    { title: 'Oat Drink Market to Reach USD 1.83 Billion by 2036 as Demand for Plant-Based Beverages and Food Service Consistency Accelerates', source: 'OpenPR' },
                    { title: 'Global Colostrum Market Set to Reach USD 6.1 Billion by 2036 as Preventive Health and Pediatric Nutrition Drive Growth', source: 'OpenPR' },
                    { title: 'It\'s Official: Data Says Plant-Based Milk Is Becoming the Default | VegNews', source: 'VegNews' },
                  ],
                },
                {
                  trend: 'up', name: 'Bridge\'s clean label focus', meta: 'Aligning with health-conscious consumers',
                  articles: [
                    { title: 'The Bridge: Italian Plant-Based Pioneer Acquired by Investment Firm Ambienta', source: 'Green Queen' },
                  ],
                },
              ],
            },
            actors: [
              { name: 'Danone', description: 'Acquired Huel for plant protein' },
              { name: 'Schouten', description: 'Launched plant-based chicken fillet' },
              {
                name: 'Redefine Meat',
                description: 'Redefine Meat has developed an industrial layered production line that allows for the efficient production of whole-muscle cuts, a significant advancement in plant-based meat technology.'
              },
              {
                name: 'Rival Foods',
                description: 'Rival Foods has partnered with THIS to launch a plant-based steak using patented Shear Cell technology, marking a significant advancement in the plant-based protein market.'
              },
              {
                name: 'Shiru',
                description: 'Ingredion and Shiru have announced a global R&D collaboration aimed at accelerating the discovery and commercialization of novel functional proteins.'
              },
            ],
          },
          upcycling: {
            title: 'Upcycled Food Radar',
            key_judgment: 'This period saw significant advancements in the upcycled food sector, particularly with LEHVOSS Nutrition and MSP Starch Products introducing an upcycled prebiotic resistant potato starch, which signals a growing market for functional ingredients derived from food waste. Additionally, Clean Food Group\'s £4.5M funding to scale yeast-derived fats highlights a strong investment trend towards sustainable ingredient production. These developments indicate that the upcycled food innovation trend is gaining ground, with increasing consumer demand for health-oriented solutions. The clear implication is to prioritize the development of products that incorporate these innovative upcycled ingredients to meet market needs and sustainability goals.',
            why_this_matters: 'LEHVOSS Nutrition and MSP Starch Products launched an upcycled prebiotic resistant potato starch. Clean Food Group secured £4.5M to scale production of yeast-derived fats. Consumer interest in natural digestive health solutions is accelerating.',
            driving_signals: {
              capital_investment: [
                {
                  trend: 'double-up', name: 'Funding for yeast-derived fats', meta: 'Clean Food Group secures £4.5M funding',
                  articles: [
                    { title: 'Clean Food Group Nets $7M in Funding to Become World\'s Largest Yeast Oil Producer', source: 'Green Queen' },
                  ],
                },
                {
                  trend: 'up', name: 'Dairy innovation ideas', meta: 'Dutch Dairy Platform seeks 50 new ideas',
                  articles: [
                    { title: 'Nieuw platform voor innovatie in de melkveehouderij', source: 'Melkveebedrijf' },
                  ],
                },
                {
                  trend: 'up', name: 'Upcycled market growth', meta: 'Projected growth to $576.72 million',
                  articles: [
                    { title: 'Building Circularity Into the Supply Chain', source: 'IFT' },
                  ],
                },
                {
                  trend: 'up', name: 'Green tech funding', meta: 'Villum Foundation grants DKK 80 million',
                  articles: [
                    { title: 'University\'s Green Tech Innovations Set for Market Growth', source: 'Mirage News' },
                  ],
                },
              ],
              technology_innovation: [
                {
                  trend: 'double-up', name: 'Upcycled prebiotic starch', meta: 'LEHVOSS Nutrition partners with MSP Starch',
                  articles: [
                    { title: 'LEHVOSS Nutrition and MSP bring upcycled prebiotic resistant potato starch to Europe', source: 'Nutrition Insight' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Scaling yeast-derived oils', meta: 'Clean Food Group expands production',
                  articles: [
                    { title: 'Clean Food Group Nets $7M in Funding to Become World\'s Largest Yeast Oil Producer', source: 'Green Queen' },
                  ],
                },
                {
                  trend: 'up', name: 'Industrial biorefinery project', meta: 'Emirates Biotech joins CIRCLE consortium',
                  articles: [
                    { title: 'Emirates Biotech Joins European CIRCLE Consortium to Produce PLA from Food Waste - Renewable Carbon News', source: 'Renewable Carbon News' },
                  ],
                },
                {
                  trend: 'up', name: 'Vitamin B2 screening method', meta: 'DTU develops new identification method',
                  articles: [
                    { title: 'From Starfish To Omega-3 Supplements', source: 'Mirage News' },
                    { title: 'New lactic acid bacteria for plant-based yoghurt alternatives', source: 'AlphaGalileo' },
                    { title: 'Bee Bacteria Could Fix a Major Flaw in Plant-Based Milk', source: 'SciTechDaily' },
                    { title: 'Bumblebee Bacterium Enables Vitamin B2 Production in Soya Drinks', source: 'ScienceMag' },
                    { title: 'A bacterium from bumblebees can produce vitamin B2 in soya drinks', source: 'AlphaGalileo' },
                  ],
                },
              ],
              policy_regulatory: [
                {
                  trend: 'up',
                  name: 'The CIRCLE project is funded by the Circular Bio-based Europe Joint Undertaking under the EU\'s Horizon Europe programme, indicating strong regulatory support for innovative waste management solutions',
                  meta: 'FrieslandCampina',
                  articles: [
                    { title: 'Emirates Biotech Joins European CIRCLE Consortium to Produce PLA from Food Waste - Renewable Carbon News', source: 'Renewable Carbon News' },
                  ],
                },
                {
                  trend: 'up',
                  name: 'The Nigerian government is actively promoting dairy production through a partnership with Nestlé to establish a training center aimed at modernizing the sector and reducing import reliance',
                  meta: 'FrieslandCampina',
                  articles: [
                    { title: 'Nestlé Nigeria Plc And Ministry Of Livestock To Establish Dairy Production Training Centre  - Oriental News Nigeria', source: 'Oriental News' },
                  ],
                },
                {
                  trend: 'neutral',
                  name: 'Regulatory support is enabling manufacturers to reduce waste and offer eco-friendly alternatives across multiple industries',
                  meta: 'Kaffe Bueno',
                  articles: [
                    { title: 'Building Circularity Into the Supply Chain', source: 'IFT' },
                    { title: 'Food Ingredients Market to Hit US$298.1 Bn by 2033 Driven by Processed Food Demand', source: 'OpenPR' },
                  ],
                },
                {
                  trend: 'up',
                  name: 'The extension of the Green X Accelerator initiative until 2031 reflects a strong policy commitment to advancing green technologies in Denmark',
                  meta: 'DTU',
                  articles: [
                    { title: 'University\'s Green Tech Innovations Set for Market Growth', source: 'Mirage News' },
                  ],
                },
              ],
              consumer_market: [
                {
                  trend: 'up', name: 'Rising prebiotic demand', meta: 'Natural digestive health solutions trending',
                  articles: [
                    { title: 'Whey\'s boom is flipping dairy economics on their head', source: 'Dairy Reporter' },
                    { title: 'Demand for Beef Flavors in USA Reach USD 1,004.91 Million by 2036 | Driven by Clean-Label Shift and Foodservice Expansion by FMI', source: 'OpenPR' },
                    { title: 'LEHVOSS Nutrition and MSP bring upcycled prebiotic resistant potato starch to Europe', source: 'Nutrition Insight' },
                  ],
                },
                {
                  trend: 'up', name: 'Prebiotics market growth', meta: 'Expected value of $23.37 billion',
                  articles: [
                    { title: 'Prebiotics Market Overview, Key Trends, and Insights on Major Players', source: 'OpenPR' },
                  ],
                },
                {
                  trend: 'up', name: 'Nutritious plant-based options', meta: 'Bacteria producing vitamin B2 identified',
                  articles: [
                    { title: 'Bee Bacteria Could Fix a Major Flaw in Plant-Based Milk', source: 'SciTechDaily' },
                  ],
                },
                {
                  trend: 'up', name: 'Ingredient transparency trend', meta: 'Consumer demand for clean labeling',
                  articles: [
                    { title: 'Persistence Market Research Pvt. Ltd. United Kingdom United States of America', source: 'Ad-Hoc News' },
                  ],
                },
              ],
            },
            actors: [
              {
                name: 'FrieslandCampina',
                description: 'The Dutch Dairy Platform aims to gather at least 50 new ideas for practical innovations in the dairy sector, supported by various organizations including FrieslandCampina.'
              },
              {
                name: 'International Flavors & Fragrances',
                description: 'International Flavors & Fragrances launched new savory flavor solutions designed for plant-based and ready-to-eat foods, focusing on clean-label ingredients that enhance taste.'
              },
              {
                name: 'Symrise',
                description: 'Symrise\'s investment in Bond Pet Foods aims to leverage precision fermentation technology to create sustainable pet food ingredients.'
              },
              {
                name: 'Meatco',
                description: 'Meatco\'s investment in Wagyu processing and advanced quality assurance systems marks a significant step towards enhancing Namibia\'s competitiveness in premium beef markets.'
              },
              { name: 'Biasol', description: 'BiaSol\'s maple crumble bar will be available for purchase on Aer Lingus flights, marking a significant partnership for the brand.' },
              {
                name: 'Feedvalid',
                description: 'The collaboration between Lidl and FeedValid aims to transform unsold food into high-quality animal feed, showcasing innovative technology in waste management.'
              },
              {
                name: 'No Waste Army',
                description: 'Aart van Oostrum is collaborating with No Waste Army to prevent the waste of 38,000 kilos of white sweet potatoes by participating in the National Potato Week.'
              },
              {
                name: 'Coroos',
                description: 'Coroos is launching Legusol®, a pulse-based ingredient aimed at hybrid meat applications, responding to the growing demand for plant-forward products.'
              },
              {
                name: 'Grassa',
                description: 'Grassa is transitioning from a start-up to a scale-up with a new brand strategy and visual identity that emphasizes sustainability and technology.'
              },
            ],
          },
          health: {
            title: 'Functional & Healthy Radar',
            key_judgment: 'The functional food and beverage market is on a clear growth trajectory, projected to nearly double to $983 billion by 2034, driven by the demand for healthier options among younger generations (signal: Consumer). This signals a shift towards products that are fortified and tailored to health needs, particularly in the context of rising GLP-1 drug usage and the focus on personalized nutrition. The recent acquisition of Bold Snacks by Ferrero underscores the urgency for traditional snack brands to innovate towards health-oriented products. Development teams should prioritize fiber-rich formulations and explore partnerships with startups to tackle formulation challenges effectively.',
            why_this_matters: 'The global functional coffee market is projected to grow significantly, driven by consumer demand for healthier beverages. Bold Snacks\' strong growth and digital strategy highlight the importance of e-commerce and direct consumer relationships. The sober-curious movement has significantly increased the demand for non-alcoholic beverages.',
            driving_signals: {
              capital_investment: [
                {
                  trend: 'double-up', name: 'Probiotic market growth', meta: 'Projected to reach USD 2.95 billion',
                  articles: [
                    { title: 'Bioniq cashes in on personalised health boom with $150M Herbalife acquisition', source: 'Tech.eu' },
                  ],
                },
                {
                  trend: 'up', name: 'Functional food ingredients market', meta: 'Expected to grow to USD 1,273 million',
                  articles: [
                    { title: 'US alt-meat slump continues, but plant protein gains traction in beverages - GFI', source: 'AgFunder News' },
                  ],
                },
                {
                  trend: 'up', name: 'Investment in plant-based companies', meta: 'Increased from $342 million to $450 million',
                  articles: [
                    { title: 'Europe Functional Food Ingredients Market [2026-2033] | Probiotics, Omega-3, Bioactive Compounds & EU Growth Expansion (2026)', source: 'OpenPR' },
                    {
                      title: 'Europe Functional Food Ingredients Market (2025-2032) | Probiotics, Food Ingredients, Specialty Additives, Nutritional Chemicals, Food Chemical & EU Growth | Major Leading Companies 2026 - BENEO GmbH, FrieslandCampina Ingredients, BENEO GmbH',
                      source: 'OpenPR'
                    },
                    { title: 'Europe Lactic Acid Market Size 2026 | Market is growing at a CAGR of 18.12% | Major Players:- Corbion, DuPont, Galactic', source: 'OpenPR' },
                    {
                      title: 'Future of Europe Sports Nutrition Market (2025) | BCAAs, Sports Supplements, Nutritional Ingredient, Supplement Chemistry | Most Leading Companies 2026 - Konzept Nutrition GmbH, Hormel Foods Corporation, NUTRICHEM DIÄT + PHARMA GMBH',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Lucrative Opportunities in Europe Dietary Supplements Market: 2026 Size, Share, Key Trends & 2034 Forecast for Investors and Stakeholders',
                      source: 'OpenPR'
                    },
                  ],
                },
                {
                  trend: 'up', name: 'Streptococcus probiotic market', meta: 'Projected to grow to USD 9,431 million',
                  articles: [
                    { title: 'Starch Manufacturing Plant DPR & Unit Setup - 2026: Machinery Cost, CapEx/OpEx, ROI and Raw Materials', source: 'OpenPR' },
                    { title: 'Why is the Functional Food Ingredients Market growing rapidly in global health and wellness industries today?', source: 'OpenPR' },
                    { title: 'Why is the Insoluble Dietary Fibers Market gaining strong demand in global digestive health and nutrition industries today?', source: 'OpenPR' },
                    {
                      title: 'Global Textured Vegetable Protein Market to Reach US$ 4.38 Billion by 2031 - DataM Intelligence Releases Definitive Market Intelligence Report',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Fructo-Oligosaccharides Market Set for Robust Growth to US$ 7.55 Billion by 2033, Driven by Rising Demand for Prebiotic and Low-Calorie Ingredients, with Food & Beverages Holding the Largest Share, Led by BENEO, Ingredion Incorporated, and Tate & Lyle PLC',
                      source: 'OpenPR'
                    },
                    {
                      title: 'United States Soluble Dietary Fibers Market 2033 | Growth Drivers, Trends & Market Forecast, Competitive Landscape & Investment Opportunities',
                      source: 'OpenPR'
                    },
                    {
                      title: 'United States Agave Inulin Market 2033 | Growth Drivers, Trends & Market Forecast, Competitive Landscape & Investment Opportunities',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Healthcare Facility Management Market Is Going to Boom |ABM Industries Inc, Sodexo, Aramark Corporation, ISS World Services A/S ECCX',
                      source: 'OpenPR'
                    },
                    { title: 'Facilities Management Market Forecast: IoT and Automation Transforming Facility Operations', source: 'OpenPR' },
                    { title: 'Catering And Food Service Contract Market Set to Expand At a Staggering 5.3% CAGR, Reaching $497.7 billion by 2032', source: 'OpenPR' },
                    { title: 'Food Ingredients Market to Hit US$298.1 Bn by 2033 Driven by Processed Food Demand', source: 'OpenPR' },
                    {
                      title: 'Polylactic Acid (PLA) Market Projected to Reach US$ 9.98 billion by 2035., Growth in Injection Molding, Blow Molding, Extrusion, Flexible Packaging, Food Service, Fibers, 3D Printing',
                      source: 'OpenPR'
                    },
                    { title: 'Dihydrolevoglucosenone Market Growing at 14.7% CAGR by 2032 | Trends, Demand & Future Scope', source: 'OpenPR' },
                    {
                      title: 'Dairy Stabilizer Market to Reach USD 1.42B by 2035 at 4.4% CAGR Led byKerry, DSM-Firmenich, DuPont, Palsgaard, Tate & Lyle, Corbion, Chr. Hansen',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Egg Replacement Ingredient Market Forecast and Outlook 2026 to 2036 | Cargill, Archer-Daniels-Midland Company, Kerry Group, Ingredion Incorporated \' | Future Market Insights \'',
                      source: 'Finance News'
                    },
                    {
                      title: 'Future Market Insights: Plant-based Protein Market Forecast and Outlook 2026 to 2036 | Cargill, Archer-Daniels-Midland Company, Kerry Group, Roquette, Ingredion Incorporated',
                      source: 'Finance News'
                    },
                    { title: 'Propionic Acid Market Predicted to Reach US$1.9 billion by 2033 at CAGR of 3.4% - Persistence Market Research', source: 'OpenPR' },
                    {
                      title: 'Biodegradable Polymers Market to Reach USD 45.5 Billion by 2035, Driven by Packaging Transition, Anti-Plastic Regulations, and Circular Economy Demand',
                      source: 'OpenPR'
                    },
                    { title: 'Food Acidulants Market to Reach US$ 4.5B by 2033, Driven by Processed Food Demand', source: 'OpenPR' },
                    { title: 'Lactic Acid Market Set to Reach US$ 6.3 Billion by 2033 Driven by Rising Demand for Biodegradable Plastics', source: 'OpenPR' },
                    {
                      title: 'Bioanalytical Testing Services Market to Reach US$ 12.24 Billion by 2033 | Growing at 12.6% CAGR Driven by Biologics and Clinical Trials Expansion',
                      source: 'OpenPR'
                    },
                    { title: 'Nisin to Revolutionize Ready-to-Eat Food Safety: Global Market Projected to Hit US$ 1.0 Billion by 2036 Amid Clean-Label Surge', source: 'OpenPR' },
                    { title: 'Mold Inhibitors Market Projected to Reach US$ 2.4 Billion by 2032', source: 'OpenPR' },
                    { title: 'Major UK supermarket launches new \'aisle\' helping shoppers make healthier choice', source: 'Birmingham Mail' },
                    {
                      title: 'Bio Active Protein Market is expected to Hit US$ 140.9 billion by 2033 | Major Companies - Archer Daniels Midland Company, Kerry Group, Cargill Inc., Omega Protein Corporation, Ajinomoto',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Frozen Food Market is expected to Hit US$ 651.10 billion in 2032 | Emerging Companies - Luvo Inc., Frozzina, Zappfresh, Banza, Amy\'s Kitchen Expansion Line',
                      source: 'OpenPR'
                    },
                    { title: 'Dairy Protein Market Outlook (2026-2033) | Driven by Sports Nutrition, Functional Foods & 9.55% CAGR Growth', source: 'OpenPR' },
                    {
                      title: 'Plant Based Protein Supplements Market is expected to Hit US$ 6.79 billion by 2032 | Major Companies - Glanbia plc., Now Health Group Inc., Danone SA, Quest The Simply Good Food Co',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Oat Protein Market is projected to Hit US$ 74.6 million by 2030 | Major Companies - Tate & Lyle PLC, Oy Karl Fazer AB, Givaudan, Lonza Group, Oat Tech, Inc.',
                      source: 'OpenPR'
                    },
                    { title: 'Global Food Stabilizers Market Dominated by Europe as Cargill and DuPont Expand Functional Ingredients', source: 'Various' },
                    { title: 'Is the Gut Microbiota Supplement Market the Next Big Opportunity in Functional Nutrition by 2033?', source: 'OpenPR' },
                    {
                      title: 'Streptococcus Probiotic Market Set for Robust Growth to US$ 9,431 Million by 2033, Driven by Rising Gut Health Awareness and Functional Nutrition Demand, with Streptococcus thermophilus Holding the Largest 50% Share, Led by Probi AB, Probiotics Internatio',
                      source: 'OpenPR'
                    },
                    { title: 'Pharma-Grade Probiotics Market Expands at 10.5% CAGR Amid Rising Therapeutic Demand', source: 'PharmiWeb' },
                    { title: 'Food Grade Pea Protein Powder: Structural Inflection from Volume Expansion to Capital Efficiency', source: 'OpenPR' },
                    { title: 'Compound Sweeteners Industry Research: growing at a CAGR of 7.3% from 2026 to 2032', source: 'OpenPR' },
                    {
                      title: 'Future Market Insights, Inc.: Global Cold Water Swelling Starch Market Set for Steady Growth as Functional Performance and Instant Solubility Drive Demand | Future Market Insights',
                      source: 'Finance News'
                    },
                    { title: 'Potato Starch Market to be Worth USD 11.4 Bn by 2036 - By Type / By Nature / By End-Use | China * India * Germany * U.S.', source: 'OpenPR' },
                    { title: 'Starch Derivatives Market Hits New High | Major Giants, ADM, Ingredion, Tate & Lyle, Roquette', source: 'OpenPR' },
                    { title: 'Psyllium Market Forecast 2025-2035: Market to Reach USD 1,120.0 million at a 4.6 % CAGR by 2035', source: 'OpenPR' },
                    { title: 'Plant Protein Market to Witness Steady CAGR of 8.6% | Industry Trends, Data & Future Outlook 2032', source: 'OpenPR' },
                    { title: 'Collagen Booster Gel Global Market 2026-2032: Size Dynamics, Key Trends and Growth Trajectory Forecast', source: 'OpenPR' },
                    {
                      title: 'Bioactive Peptides Market is expected to Hit US$ 4,114.21 Million by 2032 | Major Companies - Archer Daniels Midland Company (ADM), DSM, BASF SE, Glanbia plc',
                      source: 'OpenPR'
                    },
                    { title: 'Meal Voucher Market to Surpass USD 162.20 Billion by 2036 as Digital Adoption and Tax Incentives Drive 6.6% CAGR', source: 'OpenPR' },
                    { title: 'Fermented Proteins Market to Hit US$892.2 Mn by 2033 Driven by Alt Protein Demand', source: 'OpenPR' },
                  ],
                },
              ],
              technology_innovation: [
                {
                  trend: 'double-up', name: 'GLP-1 reformulation', meta: 'Food manufacturers experimenting with smaller portions',
                  articles: [
                    { title: 'Global Food Stabilizers Market Dominated by Europe as Cargill and DuPont Expand Functional Ingredients', source: 'Various' },
                    { title: 'How GLP-1 weight loss drugs are shaping the future of European food demand', source: 'Food Ingredients First' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Personalized nutrition focus', meta: 'Transformations in food industry',
                  articles: [
                    { title: 'Why the food industry can\'t innovate alone anymore', source: 'Bakery and Snacks' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Nutritionally rich non-alcoholic beer', meta: 'High polyphenol content',
                  articles: [
                    { title: 'R&D on health span targets aging pathways and solutions for lifelong vitality', source: 'Nutrition Insight' },
                  ],
                },
                {
                  trend: 'up', name: 'Fiber-rich food expansion', meta: 'Shift from passive to active nutrition',
                  articles: [
                    { title: 'Is it possible to create a healthy, tasty beer with no alcohol?', source: 'RTÉ' },
                  ],
                },
              ],
              policy_regulatory: [
                {
                  trend: 'up', name: 'EU GLP-1 certification', meta: 'New framework for metabolic regulators',
                  articles: [
                    { title: 'Franco-Dutch parties collaborate with Danone to advance sustainable nutrition & health', source: 'Nutrition Insight' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Sector-wide reformulation', meta: 'Response to regulatory changes',
                  articles: [
                    { title: 'Big Food\'s high\u2011stakes race to reformulate', source: 'Food Navigator' },
                  ],
                },
                {
                  trend: 'down', name: 'Novel foods regulatory challenges', meta: 'Pushback from food industry',
                  articles: [
                    { title: 'World\'s Largest Food Companies Call on EU to Include Novel Foods in Regulatory Sandboxes', source: 'Green Queen' },
                  ],
                },
                {
                  trend: 'up', name: 'Sustainable food systems initiative', meta: 'Collaboration among French and Dutch parties',
                  articles: [
                    {
                      title: '"Fibermaxxing" & Gut-Friendly Cooking Market Research Report to 2032 - Ingredion Incorporated, Cargill, Inc, Nestlé S.A., Roquette Frères and Tate & Lyle PLC',
                      source: 'OpenPR'
                    },
                  ],
                },
              ],
              consumer_market: [
                {
                  trend: 'up', name: 'Functional drinks demand', meta: 'Consumers prioritize taste and health',
                  articles: [
                    { title: 'Clean Food Group Nets $7M in Funding to Become World\'s Largest Yeast Oil Producer', source: 'Green Queen' },
                    { title: 'Äio\'s Palm Oil Substitute Makes Cosmetics Debut with Tilk\'s Skin-Boosting Serum', source: 'Green Queen' },
                    {
                      title: 'Fermented Fava Protein Market Advances at 9.4% CAGR with Glanbia, Roquette Frères, Ingredion, Cargill, ADM, PURIS, Vestkorn Milling, BENEO',
                      source: 'OpenPR'
                    },
                    { title: 'Prebiotics Market Overview, Key Trends, and Insights on Major Players', source: 'OpenPR' },
                    { title: 'M2 and Anchor Ingredients distribute mushroom powders for pet gut and immune health', source: 'Nutrition Insight' },
                    { title: 'Glucan Ingredient Market To Witness Substantial Growth, 2026-2033| Kerry Group * DSM * DuPont * Ingredion', source: 'OpenPR' },
                    { title: 'Competitive Landscape: Leading Companies and New Entrants in the Food Service Market', source: 'OpenPR' },
                    { title: 'Protein Ingredients Market Projected to Surpass USD 113 Billion by 2035 - SNS Insider', source: 'Globe Newswire' },
                    { title: 'Customized Premixes Market to Hit US$2.8 Bn by 2033 Driven by Nutrition Demand', source: 'OpenPR' },
                    {
                      title: 'Global Oleogel Bakery Fat Replacers Market Set to Surpass USD 2.1 Billion by 2036 as Trans-Fat Regulations Accelerate Clean-Label Reformulation',
                      source: 'OpenPR'
                    },
                    { title: 'Algae Protein Market to Reach USD 1.2 Billion by 2035, Driven by Sustainable Nutrition Demand and Plant-Based Innovation', source: 'OpenPR' },
                    { title: 'Global Microbiome Therapeutics Market Surges at 31.2% CAGR Amid Rising Clinical Validation and Regulatory Support', source: 'OpenPR' },
                    { title: 'Global Factors Influencing the Rapid Growth of the Microbiome Sequencing Service Market', source: 'OpenPR' },
                    { title: 'GMO Testing Market Overview, Key Trends, and Insights on Top Players', source: 'OpenPR' },
                    {
                      title: 'Protein Supplements Market hit USD$ 8.86 billion in 2026 and Poised to Reach USD$ 15.28 billion by 2033., Vita Foods Europe 2026 - GNC Holdings, LLC, Nutiva Inc, The Simply Good Food Co, Iovate Health Sciences International Inc.',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Future of Europe Sports Nutrition Market (2025) | BCAAs, Sports Supplements, Nutritional Ingredient, Supplement Chemistry | Most Leading Companies 2026 - Konzept Nutrition GmbH, Hormel Foods Corporation, NUTRICHEM DIÄT + PHARMA GMBH',
                      source: 'OpenPR'
                    },
                    { title: 'Why Leading Doctors Are Choosing This All-In-One Vegan Supplement', source: 'Plant Based News' },
                    { title: 'Key trends shaping health and wellness acquisitions & growth in nutrition industry', source: 'Nutrition Insight' },
                    {
                      title: 'Plant Based Protein Supplements Market is expected to Hit US$ 6.79 billion by 2032 | Major Companies - Glanbia plc., Now Health Group Inc., Danone SA, Quest The Simply Good Food Co',
                      source: 'OpenPR'
                    },
                    {
                      title: 'Oat Protein Market is projected to Hit US$ 74.6 million by 2030 | Major Companies - Tate & Lyle PLC, Oy Karl Fazer AB, Givaudan, Lonza Group, Oat Tech, Inc.',
                      source: 'OpenPR'
                    },
                    { title: 'Sports Nutrition Market (2026-2033) | Protein Supplements, Whey Protein, Protein Powders', source: 'OpenPR' },
                    { title: 'What makes a good protein?', source: 'Foodmanufacture' },
                    { title: 'March in review: Gut microbiome advances, weight loss innovations & Expo West 2026', source: 'Nutrition Insight' },
                    { title: 'Why Danone bought Huel and what\'s next for meal replacements', source: 'Foodchainmagazine' },
                    { title: 'Food Texture Market Growth Outlook and Strategic Analysis 2026 to 2035', source: 'OpenPR' },
                    { title: 'Market Segmentation, Dynamics, and Competitive Landscape in the Starter Cultures Industry', source: 'OpenPR' },
                    {
                      title: 'Starch and Starch Derivatives Market Size to Reach USD 105.25 Billion by 2031, Driven by Clean-Label Demand and Expanding Food Applications',
                      source: 'OpenPR'
                    },
                    { title: 'Crystalline Glucose Research:CAGR of 2.2% during the forecast period', source: 'OpenPR' },
                    { title: 'Future Food Tech San Francisco 2026: Samyang\'s crystalline kestose targets GLP-1 formulation gap', source: 'Food Ingredients First' },
                    { title: 'Future Food-Tech San Francisco 2026: Roquette tackles protein taste and texture challenges for GLP-1 consumers', source: 'Food Ingredients First' },
                    { title: 'Healthy beverage trends: A growing market for functional drinks', source: 'Food Ingredients First' },
                  ],
                },
                {
                  trend: 'down', name: 'Dairy market decline', meta: 'Oversupply and shifting preferences',
                  articles: [
                    { title: 'Can dairy beat the 2026 glut with protein and gut health focus?', source: 'Dairy Reporter' },
                  ],
                },
                {
                  trend: 'up', name: 'Sustainable nutrition research', meta: 'Partnerships addressing healthy aging',
                  articles: [
                    { title: 'Franco-Dutch parties collaborate with Danone to advance sustainable nutrition & health', source: 'Nutrition Insight' },
                  ],
                },
                {
                  trend: 'neutral', name: 'Fibermaxxing trend', meta: 'Consumers focus on dietary fiber',
                  articles: [
                    { title: 'From climate shocks to GLP-1 impacts: Why the food chain must adapt now', source: 'Food Navigator USA' },
                  ],
                },
              ],
            },
            actors: [
              { name: 'Rousselot', description: 'Advancing bioprinting technologies' },
              { name: 'Roquette', description: 'Strong performance in fiber products' },
              { name: 'Corbion', description: 'Corbion NV has received a moderate buy consensus from analysts, indicating positive investor sentiment.' },
            ],
          },
          circular: {
            title: 'Nature-Positive Food System Radar',
            key_judgment: 'This period highlights a significant shift towards regenerative agriculture practices, driven by initiatives like PepsiCo\'s expansion in Spain and Whole Foods\' launch of Regenerative Organic Certified wines. These developments indicate a growing consumer interest in sustainably sourced products, despite ongoing challenges with greenwashing and regulatory uncertainties. The trend is gaining ground, particularly in the beverage and cocoa sectors, suggesting a clear opportunity for product innovation that aligns with nature-positive principles. Companies should prioritize sourcing ingredients that support regenerative practices to meet evolving consumer expectations and regulatory demands.',
            why_this_matters: 'PepsiCo Foundation expands its regenerative agriculture programme in Spain. Whole Foods launches a line of Regenerative Organic Certified wines. Mars and Olam announce a strategic collaboration for climate-smart cocoa production.',
            driving_signals: {
              capital_investment: [
                {
                  trend: 'down', name: 'Cocoa sustainability partnership', meta: 'Mars and Olam advance regenerative practices',
                  articles: [
                    { title: 'March in review: Global fertilizer crisis, Unilever food division sale, Danone Huel acquisition', source: 'Food Ingredients First' },
                    { title: 'Higher fertiliser costs risk lower-protein wheat', source: 'Foodmanufacture' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Exceeding sustainability targets', meta: 'Purina surpasses 2025 goals for sourcing',
                  articles: [
                    { title: 'Mars and OFI partner to accelerate net-zero cocoa production in Ecuador   | FoodBev Media', source: 'FoodBev Media' },
                  ],
                },
                {
                  trend: 'double-up',
                  name: 'Purina Europe has exceeded its agricultural sustainability targets for 2025, surpassing its goal of sourcing 20% of cereals and vegetable proteins from regenerative agricultural methods',
                  meta: 'AGERpoint',
                  articles: [
                    { title: 'Purina Europe Exceeds Sustainability Targets For 2025 | ESM Magazine', source: 'ESM Magazine' },
                  ],
                },
                {
                  trend: 'down',
                  name: 'The lack of clarity from un-enacted regulations is deterring investment in supply chain improvements, which are essential for transforming the cocoa sector',
                  meta: 'Aclarity',
                  articles: [
                    { title: 'Ferrero: UK policy delays risk stalling cocoa supply chain reform as Easter demand peaks', source: 'Retail Gazette' },
                  ],
                },
              ],
              technology_innovation: [
                {
                  trend: 'double-up', name: 'Regenerative agriculture framework', meta: 'SAI Platform validates scalable practices',
                  articles: [
                    { title: 'PepsiCo Foundation expands regenerative olive farming programme in Spain  | FoodBev Media', source: 'FoodBev Media' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Modernizing olive farming', meta: 'VivaOliva promotes regenerative techniques',
                  articles: [
                    { title: 'Programme to drive regenerative farming at scale clears pilot phase', source: 'The Grocer' },
                  ],
                },
                {
                  trend: 'up',
                  name: 'The Mawgan Habitat Bank project is actively restoring habitats and creating ecological corridors, demonstrating a successful integration of development and nature conservation',
                  meta: 'THE ENVIRONMENT BANK LIMITED',
                  articles: [
                    { title: 'Major Lizard habitat scheme shows how development and nature can thrive together', source: 'South West Farmer' },
                  ],
                },
                {
                  trend: 'up',
                  name: 'The funding will enable Nature Robots to expand its team and enhance its modular autonomy software platform for agricultural machinery',
                  meta: 'Naïo Technologies',
                  articles: [
                    { title: 'Bayern Kapital backs Nature Robots\' €4M seed round for autonomous agriculture  --  TFN', source: 'Tech Funding News' },
                  ],
                },
              ],
              policy_regulatory: [
                {
                  trend: 'neutral',
                  name: 'The programme\'s rollout by the end of 2026 indicates a structured approach to enhancing sustainability in food systems',
                  meta: 'BeeOdiversity',
                  articles: [
                    { title: 'Programme to drive regenerative farming at scale clears pilot phase', source: 'The Grocer' },
                  ],
                },
                {
                  trend: 'down',
                  name: 'A report indicates that the top 20 supermarkets have made little progress in addressing their methane emissions, with no public disclosures on emissions',
                  meta: 'Climate X',
                  articles: [
                    { title: 'Top supermarkets failing to take action on methane emissions - report', source: 'Retail Systems' },
                  ],
                },
                {
                  trend: 'up',
                  name: 'The launch of the EU Sustainable Supply Chains Coalition aims to push for sustainability in EU agricultural supply chains, involving major food companies and NGOs',
                  meta: 'Climate X',
                  articles: [
                    { title: 'Kenya targets over US $700 million in waste sector Investments', source: 'Kenyanews' },
                    { title: 'EU Sustainable Supply Chains Coalition created in run up to EUDR', source: 'Food Navigator' },
                  ],
                },
                {
                  trend: 'neutral',
                  name: 'The Ulster Farmers\' Union has called for urgent engagement between retailers and the agricultural sector to address rising costs',
                  meta: 'Farm',
                  articles: [
                    { title: 'UFU calls on retailers to recognise current cost pressures on farmers', source: 'Agriland' },
                  ],
                },
              ],
              consumer_market: [
                {
                  trend: 'double-up', name: 'Expanding regenerative program', meta: 'PepsiCo supports sustainable farming',
                  articles: [
                    { title: 'PepsiCo Foundation expands regenerative olive farming programme in Spain  | FoodBev Media', source: 'FoodBev Media' },
                  ],
                },
                {
                  trend: 'double-up', name: 'Funding regeneratively grown barley', meta: 'Asahi UK invests in sustainable sourcing',
                  articles: [
                    { title: 'Asahi UK to support large-scale regenerative agriculture initiative', source: 'The Grocer' },
                  ],
                },
                {
                  trend: 'neutral',
                  name: 'The report emphasizes the increasing challenges faced by agricultural systems due to climate change, impacting both production and food availability',
                  meta: 'Climate X',
                  articles: [
                    { title: 'Ambition Into Action: How Businesses Are Embedding Sustainability Into Their DNA | ESM Magazine', source: 'ESM Magazine' },
                    {
                      title: 'Extreme heat pushing global food systems to the brink, UN agencies warn - The European Sting - Critical News & Insights on European Politics, Economy, Foreign Affairs, Business & Technology - europeansting.com United Nations News -',
                      source: 'European Sting'
                    },
                  ],
                },
                {
                  trend: 'down',
                  name: 'A review has found that 98% of sustainability pledges made by major meat and dairy companies are classified as greenwashing',
                  meta: 'Beyond Carbon',
                  articles: [
                    { title: '98 per cent of meat and dairy sustainability pledges are greenwashing', source: 'New Scientist' },
                  ],
                },
              ],
            },
            actors: [],
          },
        },
        insideItems: [
          {
            icon: 'arrow-graph',
            title: 'Market movement signals',
            desc: 'Key shifts in investment flows, partnerships, and regulatory landscape.',
          },
          {
            icon: 'arrow-expand',
            title: 'Emerging startup tracking',
            desc: 'New players raising funding, launching products, or gaining ecosystem traction.',
          },
          {
            icon: 'shield',
            title: 'Regulatory & policy updates',
            desc: 'Legislation changes and upcoming mandates that will reshape how companies operate.',
          },
          {
            icon: 'circle-nodes',
            title: 'Ecosystem connections',
            desc: 'Which organisations are collaborating and what that signals for the transition.',
          },
        ],
        latestSignals: [
          {
            icon: 'arrow-graph',
            category: 'Alternative Proteins',
            text: 'Danone\'s €1B Huel acquisition and Standing Ovation\'s €30M precision fermentation round signal a structural shift towards advanced protein technologies.',
            bullets: [
              'Danone acquires Huel for €1 billion — largest plant protein acquisition in Europe this decade',
              'Standing Ovation raises €30M to scale bioidentical casein — backed by Bel, Danone Ventures and Bpifrance',
              'Impossible Foods launches in the Netherlands, expanding European plant-based presence',
            ],
          },
          {
            icon: 'shield',
            category: 'Functional Health',
            text: 'GLP-1 drug momentum is reshaping food formulation strategies — from smaller portions to fiber-dense and protein-rich reformulation.',
            bullets: [
              'GLP-1 weight loss drugs reshaping European food demand — Roquette and Samyang targeting formulation gap',
              'Non-alcoholic beer innovation accelerating with high polyphenol and gut health credentials',
              'Personalized nutrition acquisitions rising — Bioniq acquired in $150M Herbalife deal',
            ],
          },
          {
            icon: 'arrow-expand',
            category: 'Upcycled Food',
            text: 'LEHVOSS/MSP upcycled prebiotic starch and Clean Food Group yeast fats mark a new wave of functional upcycled ingredient commercialization.',
            bullets: [
              'LEHVOSS Nutrition and MSP Starch Products launch upcycled prebiotic resistant potato starch in Europe',
              'Clean Food Group secures £4.5M to become world\'s largest yeast oil producer',
              'Emirates Biotech joins European CIRCLE consortium for PLA production from food waste',
            ],
          },
          {
            icon: 'circle-nodes',
            category: 'Nature-Positive',
            text: 'PepsiCo expands regenerative olive farming in Spain, while Whole Foods launches first domestic ROC wines — consumer demand for verified regenerative products is materializing.',
            bullets: [
              'PepsiCo Foundation expands regenerative olive farming programme in Spain',
              'Whole Foods launches Robert Hall Wines — first Regenerative Organic Certified wines on its shelves',
              'Mars and Olam partner to accelerate net-zero cocoa production in Ecuador',
            ],
          },
        ],
      }
    },

    mounted () {
      this._observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              this._observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.12 }
      )
      this.$el.querySelectorAll('.animate-on-scroll').forEach(el => {
        this._observer.observe(el)
      })
    },

    beforeUnmount () {
      if (this._observer) this._observer.disconnect()
    },
  })
</script>

<style lang="scss" scoped>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .foodleap-tp {
    font-family: 'Inter', sans-serif;
    background-color: #ffffff;
    color: #21244A;

    // ── Brand color variables ──────────────────────────────────────────────────
    $ps: #21244A; // primary slate
    $pg: #5CCC61; // primary green
    $blg: #D9E2DA; // brand light grey
    $yellow: #FFC83D; // secondary yellow (accent for this page)
    $blue: #70C1FF; // secondary blue
    $vgreen: #B5F167; // secondary vibrant green

    // ── Scroll-reveal ──────────────────────────────────────────────────────────
    .animate-on-scroll {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.6s ease, transform 0.6s ease;

      &.delay-1 {
        transition-delay: 0.12s;
      }

      &.delay-2 {
        transition-delay: 0.24s;
      }

      &.delay-3 {
        transition-delay: 0.36s;
      }

      &.is-visible {
        opacity: 1;
        transform: translateY(0);
      }
    }

    // ── Color utilities ────────────────────────────────────────────────────────
    .tp-text-slate {
      color: $ps;
    }

    .tp-text-slate-60 {
      color: rgba(33, 36, 74, 0.6);
    }

    .tp-text-slate-70 {
      color: rgba(33, 36, 74, 0.7);
    }

    .tp-text-yellow {
      color: $yellow;
    }

    .tp-bg-slate {
      background-color: $ps;
    }

    .tp-bg-light {
      background-color: rgba(217, 226, 218, 0.25);
    }

    // ── Hero ───────────────────────────────────────────────────────────────────
    .tp-hero-gradient {
      background: radial-gradient(circle at top center, rgba(255, 200, 61, 0.08) 0%, #ffffff 70%);
    }

    .tp-badge {
      background-color: rgba(255, 200, 61, 0.15);
      color: darken($yellow, 20%);
      border: 1px solid rgba(255, 200, 61, 0.3);
    }

    .tp-blob {
      background-color: $yellow;
    }

    .tp-blob-2 {
      background-color: $vgreen;
    }

    // ── Buttons ────────────────────────────────────────────────────────────────
    .tp-btn-primary {
      background-color: $ps;
      color: #ffffff;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.2);
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }

    .tp-btn-secondary {
      background-color: rgba(217, 226, 218, 0.5);
      color: $ps;
      border: 1px solid rgba(217, 226, 218, 0.8);
      transition: transform 0.2s, background-color 0.2s;

      &:hover {
        transform: translateY(-2px);
        background-color: $blg;
      }
    }

    .tp-btn-yellow {
      background-color: $yellow;
      color: $ps;
      transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 30px rgba(255, 200, 61, 0.4);
      }

      svg {
        transition: transform 0.2s;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }

    .tp-btn-unlock {
      background-color: $yellow;
      color: $ps;
      transition: transform 0.2s, box-shadow 0.2s;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 8px 20px rgba(255, 200, 61, 0.4);
      }
    }

    // ── Color shorthands for template use ──────────────────────────────────────
    .border-tp-blg {
      border-color: $blg;
    }

    .bg-tp-light-bg {
      background-color: rgba(217, 226, 218, 0.2);
    }

    .tp-text-yellow-icon {
      color: darken($yellow, 10%);
    }

    .tp-pill {
      background-color: rgba(255, 200, 61, 0.1);
      color: $ps;
      border: 1px solid rgba(255, 200, 61, 0.25);
    }

    .tp-light-bg {
      background-color: rgba(217, 226, 218, 0.25);
    }

    .tp-blur-overlay {
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.95) 40%);
    }

    .tp-preview-card {
      background-color: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .tp-btn-secondary-dark {
      background-color: rgba(255, 255, 255, 0.08);
      color: rgba(255, 255, 255, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.15);
      transition: transform 0.2s, background-color 0.2s;

      &:hover {
        transform: translateY(-2px);
        background-color: rgba(255, 255, 255, 0.14);
      }
    }

    // ── Inside-items cards ─────────────────────────────────────────────────────
    .tp-inside-card {
      background-color: #ffffff;
      border: 1px solid $blg;
      border-radius: 1.25rem;
      padding: 2rem;
      transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 16px 40px rgba(33, 36, 74, 0.08);
        border-color: rgba(255, 200, 61, 0.4);
      }
    }

    .tp-inside-icon {
      background-color: rgba(255, 200, 61, 0.12);
      color: darken($yellow, 10%);
    }

    .tp-bullet-dot {
      background-color: $yellow;
      opacity: 0.7;
    }


    // ── Pricing block ──────────────────────────────────────────────────────────
    .tp-price-card {
      background-color: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .tp-price-card-featured {
      border-color: rgba(255, 200, 61, 0.4);
      box-shadow: 0 0 0 1px rgba(255, 200, 61, 0.2);
    }

    .tp-popular-badge {
      background-color: $yellow;
      color: $ps;
    }

    .tp-check-icon {
      color: $yellow;
      flex-shrink: 0;
    }

    .tp-btn-ghost {
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.6);
      transition: border-color 0.2s, color 0.2s;

      &:hover {
        border-color: rgba(255, 255, 255, 0.5);
        color: rgba(255, 255, 255, 0.9);
      }
    }

    // ── Report switcher bar ────────────────────────────────────────────────────
    .tp-rs-bar {
      background-color: $ps;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .tp-rs-tab {
      color: rgba(255, 255, 255, 0.45);
      transition: all 0.2s;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }

    .tp-rs-active {
      background-color: $yellow;
      color: $ps !important;
    }

  }
</style>
